// Full inventory modal — Phase 7 Block C.
//
// HTML overlay (same idiom as galaxy-map-ui.ts). 30-slot grid + weapon
// equipment slot. HTML5 drag-and-drop, right-click context menu,
// hover tooltip via inventory-modal-tooltip.ts. Weapon-only for Phase 7;
// armor + offhand defer to Phase 8 (see DECISIONS).

import { balance } from "./types.ts";
import { getItemDef, items } from "../../shared/items.ts";
import type { InventorySlot } from "../../shared/messages.ts";
import {
  buildEquipItemMessage,
  buildUnequipItemMessage,
  type EquippedItemReadout,
} from "../../shared/equip-messages.ts";
import { parseItemId, QUALITY_COLORS } from "../../shared/quality.ts";
import type { ClientMessage } from "../../shared/messages.ts";
import {
  buildEquippedTooltipLines,
  buildInventoryTooltipLines,
  moveTooltipElement,
  renderTooltipElement,
} from "./inventory-modal-tooltip.ts";
import type { ServerMessage } from "../../shared/messages.ts";

// Server-message types whose arrival should trigger an inventory-modal
// rebuild while it's open (mutate inventory rows or the equipped state).
// Exposed as a constant so main.ts's onMessage hook can branch on it
// without inlining the list.
export const INVENTORY_MODAL_REFRESH_TYPES: ReadonlySet<ServerMessage["type"]> = new Set<ServerMessage["type"]>([
  "inventoryUpdated",
  "equippedItemUpdated",
  "snapshot",
  "gatherCompleted",
  "itemRepaired",
  "jobCompleted",
]);

export type InventoryModalDeps = {
  send: (msg: ClientMessage) => void;
  getInventory: () => InventorySlot[];
  getEquipped: () => EquippedItemReadout | null;
  getSkillXp: (skillId: string) => number;
  setModalSuppressed: (suppressed: boolean) => void;
};

const SLOTS_PER_ROW = balance.inventory.slotsPerRow;
const TOTAL_SLOTS = balance.inventory.maxSlots;
const ROWS = Math.ceil(TOTAL_SLOTS / SLOTS_PER_ROW);

function colorToHex(color: number): string {
  return "#" + color.toString(16).padStart(6, "0"); // contracts:allow-literal — hex string formatting
}

export class InventoryModal {
  private root: HTMLDivElement | null = null;
  private contextMenu: HTMLDivElement | null = null;
  private tooltip: HTMLDivElement | null = null;
  private _isOpen = false;
  private deps: InventoryModalDeps;

  constructor(deps: InventoryModalDeps) {
    this.deps = deps;
  }

  get isOpen(): boolean { return this._isOpen; }

  open(): void {
    if (this._isOpen) return;
    this._isOpen = true;
    this.deps.setModalSuppressed(true);
    this.root = this.buildPanel();
    document.body.appendChild(this.root);
  }

  close(): void {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.dismissContextMenu();
    this.hideTooltip();
    this.root?.remove();
    this.root = null;
    this.deps.setModalSuppressed(false);
  }

  toggle(): void { if (this._isOpen) this.close(); else this.open(); }

  refresh(): void {
    if (!this._isOpen || !this.root) return;
    const replacement = this.buildPanel();
    this.root.replaceWith(replacement);
    this.root = replacement;
  }

  private buildPanel(): HTMLDivElement {
    const root = document.createElement("div");
    root.id = "inventory-modal";
    root.style.cssText =
      "position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);" +
      "background:#1a1a2e;border:1px solid #444;border-radius:6px;" +
      "padding:16px 20px;font-family:monospace;color:#ccc;" +
      "z-index:9200;user-select:none;display:flex;gap:18px";

    root.appendChild(this.buildInventoryGrid());
    root.appendChild(this.buildEquipmentColumn());
    root.addEventListener("click", () => this.dismissContextMenu());
    return root;
  }

  private buildInventoryGrid(): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.style.cssText = "display:flex;flex-direction:column";

    const title = document.createElement("div");
    title.textContent = "Inventory";
    title.style.cssText = "font-size:14px;font-weight:bold;color:#eee;margin-bottom:10px";
    wrap.appendChild(title);

    const grid = document.createElement("div");
    grid.id = "inventory-grid";
    grid.style.cssText =
      `display:grid;grid-template-columns:repeat(${SLOTS_PER_ROW},44px);` +
      "grid-template-rows:repeat(" + String(ROWS) + ",44px);gap:4px";

    const inventory = this.deps.getInventory();
    const equipped = this.deps.getEquipped();
    const byIndex = new Map<number, InventorySlot>();
    for (const s of inventory) byIndex.set(s.slotIndex, s);

    for (let i = 0; i < TOTAL_SLOTS; i++) {
      const slot = byIndex.get(i);
      grid.appendChild(this.buildInventorySlot(i, slot, equipped));
    }
    wrap.appendChild(grid);

    const hint = document.createElement("div");
    hint.textContent = `[${balance.input.inventoryKey.replace("Key", "")}] or [Esc] close`;
    hint.style.cssText = "font-size:10px;color:#555;margin-top:10px";
    wrap.appendChild(hint);
    return wrap;
  }

  private buildInventorySlot(
    slotIndex: number,
    slot: InventorySlot | undefined,
    equipped: EquippedItemReadout | null,
  ): HTMLDivElement {
    const cell = document.createElement("div");
    cell.dataset.slotIndex = String(slotIndex);
    cell.style.cssText =
      "position:relative;width:44px;height:44px;background:#0e0e22;" +
      "border:1px solid #555;border-radius:3px;display:flex;align-items:center;" +
      "justify-content:center;cursor:default";

    if (slot === undefined) return cell;

    const itemDef = getItemDef(slot.itemId) ?? items.copper;
    const { quality } = parseItemId(slot.itemId);
    const borderColor = quality !== null ? colorToHex(QUALITY_COLORS[quality]) : "#555";
    const borderWidth = quality !== null ? "2px" : "1px"; // contracts:allow-literal — quality emphasis
    cell.style.borderColor = borderColor;
    cell.style.borderWidth = borderWidth;
    cell.style.borderStyle = "solid";

    const swatch = document.createElement("div");
    swatch.style.cssText = `width:30px;height:30px;background:${colorToHex(itemDef.color)};border-radius:2px`;
    cell.appendChild(swatch);

    const count = document.createElement("div");
    count.textContent = String(slot.count);
    count.style.cssText =
      "position:absolute;bottom:1px;right:3px;font-size:10px;color:#fff;text-shadow:1px 1px 0 #000";
    cell.appendChild(count);

    const isWeapon = itemDef.weaponType !== null;
    const isEquippedRow = equipped !== null && slot.rowid !== undefined && slot.rowid === equipped.inventoryRowid;

    if (isEquippedRow) {
      const badge = document.createElement("div");
      badge.textContent = "E";
      badge.dataset.equippedBadge = "1";
      badge.style.cssText =
        "position:absolute;top:1px;left:2px;font-size:10px;font-weight:bold;color:#88ff88;text-shadow:1px 1px 0 #000";
      cell.appendChild(badge);
    }

    if (isWeapon) {
      cell.draggable = true;
      cell.dataset.draggable = "weapon";
      cell.dataset.itemId = slot.itemId;
      cell.style.cursor = "grab";
      cell.addEventListener("dragstart", (e) => {
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", slot.itemId);
        }
      });
    }

    cell.addEventListener("mouseenter", (e) => this.showInventoryTooltip(slot, e.clientX, e.clientY));
    cell.addEventListener("mousemove", (e) => this.moveTooltip(e.clientX, e.clientY));
    cell.addEventListener("mouseleave", () => this.hideTooltip());
    cell.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.openInventoryContextMenu(e.clientX, e.clientY, slot, isEquippedRow);
    });

    return cell;
  }

  private buildEquipmentColumn(): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.style.cssText = "display:flex;flex-direction:column;align-items:center;min-width:120px";

    const title = document.createElement("div");
    title.textContent = "Equipment";
    title.style.cssText = "font-size:14px;font-weight:bold;color:#eee;margin-bottom:10px";
    wrap.appendChild(title);

    const slotLabel = document.createElement("div");
    slotLabel.textContent = "Weapon";
    slotLabel.style.cssText = "font-size:11px;color:#999;margin-bottom:4px";
    wrap.appendChild(slotLabel);

    wrap.appendChild(this.buildWeaponSlot());

    const note = document.createElement("div");
    note.textContent = "Drag a weapon here, or right-click an inventory weapon → Equip.";
    note.style.cssText = "font-size:10px;color:#666;margin-top:14px;max-width:160px;text-align:center;line-height:1.4";
    wrap.appendChild(note);
    return wrap;
  }

  private buildWeaponSlot(): HTMLDivElement {
    const slot = document.createElement("div");
    slot.id = "equipment-slot-weapon";
    slot.dataset.equipmentSlot = "weapon";
    slot.style.cssText =
      "position:relative;width:64px;height:64px;background:#0e0e22;" +
      "border:2px dashed #4a5;border-radius:4px;display:flex;align-items:center;justify-content:center";

    const equipped = this.deps.getEquipped();
    if (equipped !== null) this.fillEquippedSlot(slot, equipped);
    else this.fillEmptySlot(slot);
    this.wireDropTarget(slot);
    return slot;
  }

  private fillEquippedSlot(slot: HTMLDivElement, equipped: EquippedItemReadout): void {
    const itemDef = getItemDef(equipped.itemId) ?? items.copper;
    slot.style.borderStyle = "solid";
    slot.style.borderColor = "#88ff88";
    slot.style.cursor = "grab";
    slot.draggable = true;

    const swatch = document.createElement("div");
    swatch.style.cssText = `width:44px;height:44px;background:${colorToHex(itemDef.color)};border-radius:3px`;
    slot.appendChild(swatch);

    slot.addEventListener("dragstart", (e) => {
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", "__unequip");
      }
    });
    slot.addEventListener("mouseenter", (e) => this.showEquippedTooltip(equipped, e.clientX, e.clientY));
    slot.addEventListener("mousemove", (e) => this.moveTooltip(e.clientX, e.clientY));
    slot.addEventListener("mouseleave", () => this.hideTooltip());
    slot.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.openEquipmentContextMenu(e.clientX, e.clientY);
    });
  }

  private fillEmptySlot(slot: HTMLDivElement): void {
    const placeholder = document.createElement("div");
    placeholder.textContent = "(empty)";
    placeholder.style.cssText = "font-size:10px;color:#556";
    slot.appendChild(placeholder);
  }

  private wireDropTarget(slot: HTMLDivElement): void {
    slot.addEventListener("dragover", (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    });
    slot.addEventListener("drop", (e) => {
      e.preventDefault();
      const itemId = e.dataTransfer?.getData("text/plain") ?? "";
      if (itemId === "" || itemId === "__unequip") return;
      this.deps.send(buildEquipItemMessage(itemId));
    });
    // Drag out into the void → unequip (only the equipped weapon drags __unequip).
    slot.addEventListener("dragend", (e) => {
      if (e.dataTransfer?.dropEffect === "none" && this.deps.getEquipped() !== null) {
        this.deps.send(buildUnequipItemMessage());
      }
    });
  }

  // -----------------------------------------------------------------
  // Context menu
  // -----------------------------------------------------------------

  private openInventoryContextMenu(
    x: number,
    y: number,
    slot: InventorySlot,
    isEquippedRow: boolean,
  ): void {
    const itemDef = getItemDef(slot.itemId);
    if (itemDef === undefined || itemDef.weaponType === null) return;
    this.dismissContextMenu();
    this.contextMenu = this.buildContextMenu(x, y, [
      isEquippedRow
        ? { label: "Unequip", onClick: () => this.deps.send(buildUnequipItemMessage()) }
        : { label: "Equip", onClick: () => this.deps.send(buildEquipItemMessage(slot.itemId)) },
    ]);
    document.body.appendChild(this.contextMenu);
  }

  private openEquipmentContextMenu(x: number, y: number): void {
    this.dismissContextMenu();
    this.contextMenu = this.buildContextMenu(x, y, [
      { label: "Unequip", onClick: () => this.deps.send(buildUnequipItemMessage()) },
    ]);
    document.body.appendChild(this.contextMenu);
  }

  private buildContextMenu(
    x: number,
    y: number,
    items_: Array<{ label: string; onClick: () => void }>,
  ): HTMLDivElement {
    const menu = document.createElement("div");
    menu.dataset.contextMenu = "1";
    menu.style.cssText =
      `position:fixed;top:${y}px;left:${x}px;background:#1a1a2e;` +
      "border:1px solid #4455cc;border-radius:3px;padding:4px 0;" +
      "z-index:9300;font-family:monospace;font-size:12px;color:#eef;" +
      "min-width:120px;box-shadow:0 4px 12px rgba(0,0,0,0.6)";
    for (const it of items_) {
      const row = document.createElement("div");
      row.textContent = it.label;
      row.dataset.contextAction = it.label.toLowerCase();
      row.style.cssText = "padding:6px 12px;cursor:pointer";
      row.addEventListener("mouseenter", () => { row.style.background = "#2a2a55"; });
      row.addEventListener("mouseleave", () => { row.style.background = "transparent"; });
      row.addEventListener("click", (e) => {
        e.stopPropagation();
        it.onClick();
        this.dismissContextMenu();
      });
      menu.appendChild(row);
    }
    return menu;
  }

  private dismissContextMenu(): void {
    this.contextMenu?.remove();
    this.contextMenu = null;
  }

  // -----------------------------------------------------------------
  // Tooltip
  // -----------------------------------------------------------------

  private showInventoryTooltip(slot: InventorySlot, x: number, y: number): void {
    const lines = buildInventoryTooltipLines(slot, this.deps.getSkillXp);
    if (lines.length === 0) return;
    this.renderTooltip(renderTooltipElement(lines), x, y);
  }

  private showEquippedTooltip(equipped: EquippedItemReadout, x: number, y: number): void {
    const lines = buildEquippedTooltipLines(equipped);
    if (lines.length === 0) return;
    this.renderTooltip(renderTooltipElement(lines), x, y);
  }

  private renderTooltip(el: HTMLDivElement, x: number, y: number): void {
    this.hideTooltip();
    document.body.appendChild(el);
    this.tooltip = el;
    moveTooltipElement(el, x, y);
  }

  private moveTooltip(x: number, y: number): void {
    if (this.tooltip) moveTooltipElement(this.tooltip, x, y);
  }

  private hideTooltip(): void {
    this.tooltip?.remove();
    this.tooltip = null;
  }
}
